import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { LocationSelectors } from '@core/locations-data';
import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';
import type {
  InsertFromTemplateMethod,
  OfferData,
  OfferFull,
  OfferStatus,
  StatusChangeType,
} from '@core/offers-types';
import { PlacementMovement } from '@core/stages-types';

import { FetchOneJobsFullActionContext, OfferScope, EntityStoreKey } from '../../../types';

const {
  actions: defaultActions,
  namespace,
  selectors,
  reducer,
} = createEntityCollectionStore<OfferFull>()(EntityStoreKey.JobsFull, {
  references: makeRef => [makeRef('locationIds', 'locations', LocationSelectors.byIds)],
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => ({
    fetchOne: (
      state,
      payload: { id: number | string; langCode?: string; context: FetchOneJobsFullActionContext },
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
    fetchOneSuccess: (
      state,
      payload: { id: number | string; job: OfferFull; context: FetchOneJobsFullActionContext },
    ) =>
      pipeState(
        state,
        next => adapter.upsertOne(next, payload.job),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
      ),
    fetchOneFailed: (
      state,
      payload: {
        id: number | string;
        error: HttpErrorResponse;
        context: FetchOneJobsFullActionContext;
      },
    ) => adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),

    update: (state, payload: { offer: OfferData & { id: number } }) =>
      pipeState(adapter.markAsPending(state, { scope: null, entities: [payload.offer.id] })),
    updateSuccess: (state, payload: { id: number; data: OfferFull }) =>
      pipeState(adapter.updateOne(state, payload.data), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
      ),
    updateFailed: (
      state,
      payload: { id: number; error: HttpErrorResponse & { errorFields: any } },
    ) => pipeState(adapter.setError(state, payload.error, { scope: null, entities: [payload.id] })),

    follow: (state, payload: { id: number }) =>
      adapter.markAsPending(state, {
        scope: null,
        entities: [payload.id],
      }),
    followSuccess: (state, payload: { job: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.job), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.job.id] }),
      ),
    followFailed: (state, payload: { error: HttpErrorResponse; id: number }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),

    unfollow: (state, payload: { id: number }) =>
      adapter.markAsPending(state, {
        scope: null,
        entities: [payload.id],
      }),
    unfollowSuccess: (state, payload: { job: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.job), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.job.id] }),
      ),
    unfollowFailed: (state, payload: { error: HttpErrorResponse; id: number }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),

    duplicate: ngrxEvent<{ id: number }>(),
    duplicateSuccess: (state, payload: { offer: OfferFull }) =>
      adapter.upsertOne(state, payload.offer),
    duplicateFailed: ngrxEvent<{ id: number; error: HttpErrorResponse & { errorFields: any } }>(),

    create: ngrxEvent<{ offer: OfferData }>(),
    createSuccess: (state, payload: { data: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.data)),
    createFailed: ngrxEvent<{ error: HttpErrorResponse & { errorFields: any } }>(),

    createFromRequisition: ngrxEvent<{ requisitionId: number }>(),
    createFromRequisitionSuccess: (state, payload: { data: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.data)),
    createFromRequisitionFailed: ngrxEvent<{ error: HttpErrorResponse & { errorFields: any } }>(),

    createFromTemplate: ngrxEvent<{ templateId: number }>(),
    createFromTemplateSuccess: (state, payload: { data: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.data)),
    createFromTemplateFailed: ngrxEvent<{ error: HttpErrorResponse & { errorFields: any } }>(),

    createFromPosition: ngrxEvent<{ positionId: number }>(),
    createFromPositionSuccess: (state, payload: { data: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.data)),
    createFromPositionFailed: ngrxEvent<{ error: HttpErrorResponse & { errorFields: any } }>(),

    changeStatus: (
      state,
      payload: {
        id: number;
        status: StatusChangeType;
        currentStatus: OfferStatus;
      },
    ) =>
      adapter.markAsPending(state, {
        scope: null,
        entities: [payload.id],
      }),
    changeStatusSuccess: (
      state,
      payload: { offer: OfferFull; targetScope: OfferScope; isChangingScope: boolean },
    ) =>
      pipeState(adapter.upsertOne(state, payload.offer), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.offer.id] }),
      ),
    changeStatusFailed: (
      state,
      payload: { error: HttpErrorResponse & { errorFields: any }; id: number },
    ) => adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),

    preview: ngrxEvent<{ offer: OfferData }>(),
    previewSuccess: ngrxEvent<{ previewUrl: string }>(),
    previewFailed: ngrxEvent<{
      error: HttpErrorResponse;
    }>(),

    uploadSocialMediaImage: ngrxEvent<{ id: number; file: File }>(),
    uploadSocialMediaImageSuccess: ngrxEvent<{ fileName: string; url: string }>(),
    uploadSocialMediaImageFailed: ngrxEvent<{
      id: number;
      error: HttpErrorResponse;
    }>(),

    uploadCoverImage: ngrxEvent<{ id: number; file: File }>(),
    uploadCoverImageSuccess: ngrxEvent<{ fileName: string; url: string }>(),
    uploadCoverImageFailed: ngrxEvent<{
      id: number;
      error: HttpErrorResponse;
    }>(),

    updateSlug: (state, payload: { id: number; slug: string }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
    updateSlugSuccess: (state, payload: { id: number; offer: OfferFull }) =>
      pipeState(
        state,
        next => adapter.updateOne(next, payload.offer),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
      ),
    updateSlugFailed: ngrxEvent<{
      id: number;
      error: HttpErrorResponse;
    }>(),

    insertDataFromTemplate: (
      state,
      payload: {
        offerId: number;
        jobTemplateId: number;
        insertMethod: InsertFromTemplateMethod;
        placementsMovements?: PlacementMovement[];
      },
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.offerId] }),
    insertDataFromTemplateSuccess: (state, payload: { data: { offer: OfferFull } }) =>
      adapter.markAsLoaded(state, { scope: null, entities: [payload.data.offer.id] }),
    insertDataFromTemplateFailed: (state, payload: { error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error),
  }),
});

const bySlug = memoizeSelector((slug: string) =>
  createSelector(selectors.entities({ scope: null }), (state: OfferFull[]) =>
    state.find(offer => offer.slug === slug),
  ),
);
const bySlugWithReferences = memoizeSelector((slug: string) =>
  createSelector(selectors.entitiesWithReferences({ scope: null }), (state: OfferFull[]) =>
    state.find(offer => offer.slug === slug),
  ),
);

const actions = {
  fetchOne: defaultActions.fetchOne,
  fetchOneFailed: defaultActions.fetchOneFailed,
  fetchOneSuccess: defaultActions.fetchOneSuccess,
  update: defaultActions.update,
  updateFailed: defaultActions.updateFailed,
  updateSuccess: defaultActions.updateSuccess,
  follow: defaultActions.follow,
  followFailed: defaultActions.followFailed,
  followSuccess: defaultActions.followSuccess,
  unfollow: defaultActions.unfollow,
  unfollowFailed: defaultActions.unfollowFailed,
  unfollowSuccess: defaultActions.unfollowSuccess,
  duplicate: defaultActions.duplicate,
  duplicateFailed: defaultActions.duplicateFailed,
  duplicateSuccess: defaultActions.duplicateSuccess,
  create: defaultActions.create,
  createFailed: defaultActions.createFailed,
  createSuccess: defaultActions.createSuccess,
  createFromRequisition: defaultActions.createFromRequisition,
  createFromRequisitionFailed: defaultActions.createFromRequisitionFailed,
  createFromRequisitionSuccess: defaultActions.createFromRequisitionSuccess,
  createFromTemplate: defaultActions.createFromTemplate,
  createFromTemplateFailed: defaultActions.createFromTemplateFailed,
  createFromTemplateSuccess: defaultActions.createFromTemplateSuccess,
  createFromPosition: defaultActions.createFromPosition,
  createFromPositionFailed: defaultActions.createFromPositionFailed,
  createFromPositionSuccess: defaultActions.createFromPositionSuccess,
  changeStatus: defaultActions.changeStatus,
  changeStatusFailed: defaultActions.changeStatusFailed,
  changeStatusSuccess: defaultActions.changeStatusSuccess,
  removeOne: defaultActions.removeOne,
  preview: defaultActions.preview,
  previewSuccess: defaultActions.previewSuccess,
  previewFailed: defaultActions.previewFailed,
  uploadSocialMediaImage: defaultActions.uploadSocialMediaImage,
  uploadSocialMediaImageSuccess: defaultActions.uploadSocialMediaImageSuccess,
  uploadSocialMediaImageFailed: defaultActions.uploadSocialMediaImageFailed,
  uploadCoverImage: defaultActions.uploadCoverImage,
  uploadCoverImageSuccess: defaultActions.uploadCoverImageSuccess,
  uploadCoverImageFailed: defaultActions.uploadCoverImageFailed,
  updateSlug: defaultActions.updateSlug,
  updateSlugSuccess: defaultActions.updateSlugSuccess,
  updateSlugFailed: defaultActions.updateSlugFailed,
  insertDataFromTemplate: defaultActions.insertDataFromTemplate,
  insertDataFromTemplateSuccess: defaultActions.insertDataFromTemplateSuccess,
  insertDataFromTemplateFailed: defaultActions.insertDataFromTemplateFailed,
};

const JobsFullSelectors = {
  ...selectors,
  bySlug,
  bySlugWithReferences,
};

export {
  actions as JobsFullActions,
  JobsFullSelectors,
  namespace as JobsFullNamespace,
  reducer as JobsFullReducer,
};
